import React, { useEffect, useState } from "react";

import {
  CORNER_TEMPLATE_TITLES,
  dayName,
  timeWithSuffix,
  monthName,
  parseDate,
  todayIso,
} from "./competition.constants";
import { CORNER_TEMPLATES } from "./competition.theme";

const DAY_MS = 24 * 60 * 60 * 1000;
const MINUTES_PER_DAY = 24 * 60;

/** Türkiye saatiyle şu an: tarih (YYYY-MM-DD) ve gece yarısından beri geçen dakika. */
const istanbulNow = () => {
  const parts = {};
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  })
    .formatToParts(new Date())
    .forEach((part) => {
      parts[part.type] = part.value;
    });
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    minutes: Number(parts.hour) * 60 + Number(parts.minute),
  };
};

/** Yarım dakikada bir yenilenen Türkiye saati (kalan süreler için). */
const useIstanbulNow = () => {
  const [now, setNow] = useState(istanbulNow);
  useEffect(() => {
    const timer = setInterval(() => setNow(istanbulNow()), 30 * 1000);
    return () => clearInterval(timer);
  }, []);
  return now;
};

const toMinutes = (hhmm) => {
  if (!hhmm) return null;
  const [hour, minute] = String(hhmm).split(":").map(Number);
  return Number.isNaN(hour) || Number.isNaN(minute) ? null : hour * 60 + minute;
};

/**
 * Vaktin (imsak / iftar) bir sonraki gerçekleşmesi: bugünkü henüz gelmediyse o, geçtiyse
 * yarınki. Yarının kaydı yoksa null.
 */
const nextTime = (key, todayRow, tomorrowRow, nowMinutes) => {
  const today = toMinutes(todayRow?.[key]);
  if (today !== null && nowMinutes < today) {
    return { time: todayRow[key], remaining: today - nowMinutes };
  }
  const tomorrow = toMinutes(tomorrowRow?.[key]);
  if (tomorrow !== null) {
    return {
      time: tomorrowRow[key],
      remaining: MINUTES_PER_DAY - nowMinutes + tomorrow,
    };
  }
  return null;
};

const remainingText = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} dakika kaldı`;
  if (rest === 0) return `${hours} saat kaldı`;
  return `${hours} saat ${rest} dakika kaldı`;
};

/** "İSTANBUL" / "AFYONKARAHİSAR" → "İstanbul" / "Afyonkarahisar" (Türkçe i/İ kurallarıyla). */
const titleCaseTr = (value) =>
  value
    ? value
        .toLocaleLowerCase("tr-TR")
        .replace(
          /(^|[\s-])(\S)/g,
          (match, sep, letter) => sep + letter.toLocaleUpperCase("tr-TR")
        )
    : value;

const Bar = ({ value }) => (
  <div className="cmp-corner-bar">
    <span style={{ width: `${Math.max(0, Math.min(100, value * 100))}%` }} />
  </div>
);

const todayStatus = (competition) => {
  switch (competition.todayState) {
    case "OPEN":
      return "Bugünün sorusu sizi bekliyor!";
    case "DONE":
      return "Bugünün sorusunu yanıtladınız. Yarın görüşmek üzere!";
    case "NOT_STARTED":
      return `Yarışma bugün saat ${timeWithSuffix(competition.todayOpensAt)} başlıyor.`;
    case "NO_QUESTION":
      return "Bugün için soru bulunmuyor.";
    default:
      return "Bugünün yarışması sona erdi.";
  }
};

/**
 * Yarışma Köşesi: günün tarihi, yarışmanın kaçıncı günü olduğu ve admin'in o güne girdiği
 * içerikler (sekmeli). İmsak / iftar vakitleri girilmişse (fasting_time tablosu) sayaç kutusunda
 * vakitler ve kalan süreler gösterilir. Buton kayıt oluşturmaz; yalnızca yarışma sayfasına götürür.
 *
 * Köşe şablonu sabittir ve ön yüze gömülüdür: web'de yatay, mobilde dikey şablon görseli
 * kullanılır; içerik görseldeki kutulara yazılır, "TIKLAYIN" metni görselin içindedir.
 * decorations: admin'in sol üst / sağ üst süsleri (şablonun üst köşelerine yerleşir).
 * menu: yarışma menüsü; şablonlu köşede diğer sayfalardaki gibi görselin üstüne yerleşir.
 */
const CornerView = ({ competition, decorations, menu, onGoToCompetition }) => {
  const corner = competition.corner;
  const template = CORNER_TEMPLATES[corner?.template];
  const items = corner?.items || [];
  const [active, setActive] = useState(0);

  const today = parseDate(todayIso());
  const start = parseDate(competition.dateBegin);
  const end = parseDate(competition.dateEnd);
  const inRange = start && end && today >= start && today <= end;
  const dayNo = inRange ? Math.round((today - start) / DAY_MS) + 1 : null;
  const daysLeft = inRange ? Math.round((end - today) / DAY_MS) : null;
  const current = items[Math.min(active, items.length - 1)];
  const now = useIstanbulNow();
  const fastingTimes = corner?.fastingTimes || [];
  const todayRow = fastingTimes.find((row) => row.date === now.date);
  const tomorrowRow = fastingTimes.find((row) => row.date > now.date);
  const imsak = nextTime("imsak", todayRow, tomorrowRow, now.minutes);
  const iftar = nextTime("iftar", todayRow, tomorrowRow, now.minutes);
  const city = titleCaseTr((todayRow || tomorrowRow)?.city);
  const isRamadan = corner?.template === "RAMADAN";
  // Yarışma Ramazan'ın son günü bittiği için bayram, bitiş tarihinin ertesi günüdür.
  const bayramIn = daysLeft !== null ? daysLeft + 1 : null;
  let dayText;
  if (isRamadan) {
    dayText =
      bayramIn > 1
        ? `Ramazan Bayramı'na ${bayramIn} gün kaldı`
        : "Yarın Ramazan Bayramı";
  } else {
    dayText =
      daysLeft > 0
        ? `Yarışmanın bitmesine ${daysLeft} gün kaldı`
        : "Yarışmanın son günü";
  }
  // Şablonlu köşede buton metni görselin içindedir; şablonsuz köşede butonda yazılır.
  const ctaText = "Yarışmaya katılmak için TIKLAYIN!";

  const dateBox = (
    <div className="cmp-corner-date">
      <span className="cmp-corner-month">{monthName(today)}</span>
      <span className="cmp-corner-day">
        {String(today.getDate()).padStart(2, "0")}
      </span>
      <span className="cmp-corner-weekday">{dayName(today)}</span>
      {city && <span className="cmp-corner-city">{city}</span>}
    </div>
  );

  const fastingBox = (
    <div className="cmp-corner-count cmp-corner-count-fasting">
      {dayNo && (
        <div className="cmp-corner-fast-row">
          <div className="cmp-corner-fast-head">
            <strong>{dayNo}.GÜN</strong>
            <span>{dayText}</span>
          </div>
          <Bar value={dayNo / (dayNo + (daysLeft || 0))} />
        </div>
      )}
      {[
        ["İmsak", imsak],
        ["İftar", iftar],
      ].map(
        ([label, value]) =>
          value && (
            <div className="cmp-corner-fast-row" key={label}>
              <div className="cmp-corner-fast-line">
                <div>
                  <span className="cmp-corner-fast-label">{label}</span>
                  <span className="cmp-corner-fast-left">
                    {remainingText(value.remaining)}
                  </span>
                </div>
                <span className="cmp-corner-fast-time">{value.time}</span>
              </div>
              <Bar value={1 - value.remaining / MINUTES_PER_DAY} />
            </div>
          )
      )}
    </div>
  );

  const countBox = (
    <div className="cmp-corner-count">
      {dayNo ? (
        <>
          <div className="cmp-corner-count-text">
            <strong>{dayNo}. GÜN</strong>
            <span>
              {daysLeft > 0
                ? `Yarışmanın bitmesine ${daysLeft} gün kaldı`
                : "Yarışmanın son günü"}
            </span>
          </div>
          <div className="cmp-progress">
            <span
              style={{
                width: `${Math.min(100, (dayNo / (dayNo + (daysLeft || 0))) * 100)}%`,
              }}
            />
          </div>
        </>
      ) : null}
      <p className="cmp-corner-status">{todayStatus(competition)}</p>
    </div>
  );

  const contentBox = (
    <div className="cmp-corner-content">
      {items.length === 0 ? (
        <p className="cmp-corner-text">Bugünün köşe içeriği henüz eklenmedi.</p>
      ) : (
        <>
          <div className="cmp-corner-tabs" role="tablist">
            {items.map((item, index) => (
              <button
                key={item.field}
                type="button"
                role="tab"
                aria-selected={index === active}
                className={index === active ? "active" : ""}
                onClick={() => setActive(index)}
              >
                {item.title}
              </button>
            ))}
          </div>
          <div className="cmp-corner-text">{current?.text}</div>
        </>
      )}
    </div>
  );

  if (template) {
    /*
     * Aynı içerik iki şablona da yazılır; medya sorgusu hangisinin görüneceğini seçer. Gizlenen
     * sarmalayıcı olduğu için tarayıcı görünmeyen şablonun görselini indirmez.
     */
    const renderArt = (className, image) => (
      <div className={className}>
        <div
          className="cmp-corner-art"
          style={{ backgroundImage: `url(${image})` }}
        >
          {decorations}
          {menu}
          <div className="cmp-corner-slot cmp-corner-slot-date">{dateBox}</div>
          <div className="cmp-corner-slot cmp-corner-slot-count">
            {imsak || iftar ? fastingBox : countBox}
          </div>
          <div className="cmp-corner-slot cmp-corner-slot-content">
            {contentBox}
          </div>
          <button
            type="button"
            className="cmp-corner-slot cmp-corner-slot-cta"
            aria-label={ctaText}
            onClick={onGoToCompetition}
          />
        </div>
      </div>
    );

    return (
      <div className="cmp-corner cmp-corner-templated">
        {renderArt("cmp-corner-art-web", template.art)}
        {renderArt("cmp-corner-art-mobile", template.mobileArt)}
      </div>
    );
  }

  return (
    <div className="cmp-corner">
      {menu}
      <h2 className="cmp-corner-title">
        {CORNER_TEMPLATE_TITLES[corner?.template] || "Yarışma Köşesi"}
      </h2>
      <div className="cmp-corner-top">
        <div className="cmp-glass">{dateBox}</div>
        <div className="cmp-glass">{countBox}</div>
      </div>
      <div className="cmp-glass">{contentBox}</div>
      <div className="cmp-corner-cta-wrap">
        <button
          type="button"
          className="cmp-corner-cta"
          onClick={onGoToCompetition}
        >
          {ctaText}
        </button>
      </div>
    </div>
  );
};

export default CornerView;
